/**
 * <FAQSection> — public-homepage FAQ block.
 *
 * Same Q&A copy as the FAQPage JSON-LD in /public/index.html so what Google
 * sees in structured data matches what users see on-page (Google penalises
 * mismatches between rendered FAQ and JSON-LD FAQ).
 *
 * Accessible: each Q is a proper <button> inside an <h3>, with aria-expanded
 * + aria-controls, so screen readers + keyboard navigation work cleanly.
 */
import React, { useState } from 'react';
import { ChevronDown, HelpCircle, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { track } from '../services/analytics';

const FAQS = [
  {
    q: 'How can I access RentKollect?',
    a: 'RentKollect runs on the web today — sign in at rentkollect.com from any phone or laptop browser, no install needed. A native iOS + Android mobile app is launching shortly (early-access waitlist open). For owners who prefer to skip dashboards, our AI assistant Kolly also accepts commands over WhatsApp.',
  },
  {
    q: 'Who is RentKollect built for?',
    a: 'Indian PG (paying-guest), hostel, and co-living property owners — from a single 10-bed PG to chains with 20+ properties. We focus on Indian operational reality: INR pricing, Aadhaar e-KYC, UPI collection, GST invoicing, and Hinglish-friendly interfaces.',
  },
  {
    q: 'What modules are included in RentKollect?',
    a: 'Ten production-grade modules in one platform — Money (rent, dues, deposits, expenses, P&L), Properties & Rooms, Tenants & KYC, Leads & Bookings CRM, Operations (complaints, food, cleaning, assets, visitors), Smart Reminders, Reports & Analytics, Public Listings, E-Sign Agreements, and WhatsApp Automation. No per-module add-on pricing.',
  },
  {
    q: 'How much does RentKollect cost?',
    a: 'Free for the first 30 days with every feature unlocked. No credit card required at signup. After the trial, pricing is a single per-property monthly subscription that covers all 10 modules — no add-on charges. Reach out via WhatsApp +91 98219 22296 or hello@rentkollect.com for current rates.',
  },
  {
    q: 'How do I sign up?',
    a: 'Visit rentkollect.com and click "Sign Up" — you can register with just a mobile number and OTP. We will WhatsApp your login link and onboard you in under 10 minutes. Prefer WhatsApp? Message "Hi" to +91 98219 22296 and Kolly will set you up.',
  },
  {
    q: 'Can I manage multiple properties from one account?',
    a: 'Yes. Add unlimited properties under a single owner login. Each property has its own occupancy %, collection, expenses, and P&L — but you also get a consolidated dashboard across all of them. Different bank account or UPI VPA per property is supported for clean accounting.',
  },
  {
    q: 'Does RentKollect handle rent agreements and tenant KYC?',
    a: 'Yes. Capture tenant Aadhaar (front + back + selfie) from any phone — flagged automatically if KYC is pending. Rent agreements auto-fill from the tenant profile and can be sent for legally valid Aadhaar e-sign via Leegality. The signed PDF is stored permanently against the tenant record with a full audit trail.',
  },
  {
    q: 'What languages does RentKollect support?',
    a: 'The web app and (upcoming) mobile app support English and Hindi with a one-click toggle. Our WhatsApp assistant Kolly additionally understands Hinglish (Hindi typed in Roman script) and auto-detects your preferred language from the first message.',
  },
  {
    q: 'When is the mobile app launching?',
    a: 'The native Android and iOS apps are in active build, targeted for public release in the next few weeks. The same login works across web and mobile, and all your data syncs in real time. Owners on rentkollect.com today get early access automatically.',
  },
  {
    q: 'Who operates RentKollect?',
    a: 'RentKollect is a brand owned and operated by Ramde Business Solutions LLP — an Indian limited liability partnership registered in Gurgaon, Haryana. LLPIN: ACR-9891. GSTIN: 06ABMFR3856J1ZO.',
  },
  {
    q: 'Is my data secure and compliant with Indian privacy laws?',
    a: "Yes. RentKollect is compliant with India's Digital Personal Data Protection Act 2023 and EU GDPR. Data is hosted in India, encrypted in transit and at rest, and never sold to third parties. Account and data deletion can be requested anytime at rentkollect.com/data-deletion.",
  },
];

const FAQSection = () => {
  // First item open by default so the section visually communicates its purpose
  // without forcing the user to interact.
  const [openIndex, setOpenIndex] = useState(0);

  const toggle = (idx) => {
    const willOpen = openIndex !== idx;
    setOpenIndex(willOpen ? idx : -1);
    if (willOpen) {
      track('faq_item_opened', { question: FAQS[idx].q, index: idx });
    }
  };

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-20 bg-white"
      aria-labelledby="faq-heading"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-blue-700 bg-blue-50 px-3 py-1 rounded-full ring-1 ring-blue-100">
            <HelpCircle size={12} /> Frequently asked
          </span>
          <h2
            id="faq-heading"
            className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight"
          >
            Questions PG owners ask us
          </h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto">
            Quick answers about how RentKollect works, pricing, languages, and data security.
            Don&apos;t see your question? WhatsApp us — we usually reply within an hour.
          </p>
        </div>

        <div className="space-y-3" role="list">
          {FAQS.map((faq, idx) => {
            const isOpen = openIndex === idx;
            const panelId = `faq-panel-${idx}`;
            const btnId   = `faq-trigger-${idx}`;
            return (
              <div
                key={faq.q}
                role="listitem"
                className={`rounded-2xl border transition-all ${
                  isOpen ? 'border-blue-200 bg-blue-50/40 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                data-testid={`faq-item-${idx}`}
              >
                <h3 className="m-0">
                  <button
                    id={btnId}
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggle(idx)}
                    data-testid={`faq-trigger-${idx}`}
                    className="w-full flex items-start justify-between gap-4 text-left p-5 sm:p-6 group"
                  >
                    <span className="text-base sm:text-lg font-semibold text-gray-900 leading-snug">
                      {faq.q}
                    </span>
                    <ChevronDown
                      size={20}
                      aria-hidden="true"
                      className={`flex-shrink-0 mt-1 text-gray-500 group-hover:text-blue-700 transition-transform duration-200 ${
                        isOpen ? 'rotate-180 text-blue-700' : ''
                      }`}
                    />
                  </button>
                </h3>
                {isOpen && (
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={btnId}
                    data-testid={`faq-panel-${idx}`}
                    className="px-5 sm:px-6 pb-5 sm:pb-6 -mt-1"
                  >
                    <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer prompt */}
        <div className="mt-10 p-5 rounded-2xl bg-gradient-to-r from-emerald-50 to-blue-50 ring-1 ring-emerald-100 flex flex-col sm:flex-row items-center gap-4 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
              <MessageCircle size={20} />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Still have questions?</p>
              <p className="text-sm text-gray-600">WhatsApp us — we usually reply within an hour.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <a
              href="https://wa.me/919821922296?text=Hi%2C%20I%20have%20a%20question%20about%20RentKollect"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track('cta_faq_whatsapp_clicked')}
              data-testid="faq-whatsapp-cta"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition-colors"
            >
              <MessageCircle size={16} /> Chat on WhatsApp
            </a>
            <Link
              to="/contact"
              onClick={() => track('cta_faq_contact_clicked')}
              data-testid="faq-contact-cta"
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-white ring-1 ring-gray-300 hover:ring-gray-400 text-gray-800 font-semibold text-sm transition-colors"
            >
              Contact form
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
